import React,{useState} from 'react';
import {Link} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SearchBar from '../Search/SearchBar';
import {useTheme} from '../../contexts/ThemeContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {useAuth} from '../../contexts/AuthContext';

const {FiMenu,FiSun,FiMoon,FiBookmark,FiSettings,FiUndo,FiLogOut,FiChevronLeft,FiChevronRight,FiX}=FiIcons;

const Header=({onMobileMenuClick,onTabletToggle,mobileMenuOpen,tabletSidebarCollapsed})=> {
  const {theme,toggleTheme}=useTheme();
  const {undoStack,undo}=useBookmarks();
  const {user,signOut}=useAuth();
  const [showUserMenu,setShowUserMenu]=useState(false);

  return (
    <header className={`sticky top-0 z-40 border-b pt-[env(safe-area-inset-top,0px)] transition-colors duration-300 glass-effect ${theme==='dark' ? 'bg-zinc-950/90 border-zinc-800' : 'bg-white/90 border-gray-200 shadow-sm'}`}>
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-3 sm:px-6 py-3 h-16">
        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
          <button
            onClick={onMobileMenuClick}
            className={`lg:hidden p-2.5 rounded-xl transition-colors flex-shrink-0 ${theme==='dark' ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-600'}`}
            aria-label="Toggle Menu"
          >
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-5 h-5" />
          </button>

          <button
            onClick={onTabletToggle}
            className={`hidden lg:flex p-2.5 rounded-xl transition-colors ${theme==='dark' ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-600'}`}
            aria-label="Toggle Sidebar"
          >
            <SafeIcon icon={tabletSidebarCollapsed ? FiChevronRight : FiChevronLeft} className="w-5 h-5" />
          </button>

          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group min-w-0">
            <div className="p-1.5 sm:p-2 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-500/30 flex-shrink-0">
              <SafeIcon icon={FiBookmark} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="hidden xs:block truncate">
              <h1 className="text-sm sm:text-lg font-black tracking-tighter leading-none">BOOKMARKS</h1>
              <p className="text-[8px] sm:text-[10px] font-bold text-indigo-500 tracking-[0.2em] leading-none mt-1">PRO MANAGER</p>
            </div>
          </Link>
        </div>

        <div className="flex-1 max-w-xl mx-4 xl:mx-8 hidden lg:block">
          <SearchBar />
        </div>

        <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
          <AnimatePresence>
            {undoStack.length >0 && (
              <motion.button
                initial={{opacity: 0,y: -4}}
                animate={{opacity: 1,y: 0}}
                exit={{opacity: 0,y: -4}}
                onClick={undo}
                className={`p-2.5 rounded-xl transition-colors ${theme==='dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}
                title="Undo"
              >
                <SafeIcon icon={FiUndo} className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            )}
          </AnimatePresence>

          <button
            onClick={toggleTheme}
            className={`p-2.5 rounded-xl transition-colors ${theme==='dark' ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-600'}`}
            aria-label={theme==='dark' ? 'Use light theme' : 'Use dark theme'}
          >
            <SafeIcon icon={theme==='dark' ? FiSun : FiMoon} className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <div className="h-6 w-[1px] bg-zinc-800/10 dark:bg-zinc-800 mx-1 sm:mx-2 hidden xs:block" />

          <div className="relative">
            <button
              onClick={()=> setShowUserMenu(!showUserMenu)}
              className={`flex items-center space-x-2 p-1 rounded-full transition-colors border ${theme==='dark' ? 'border-zinc-800 hover:bg-zinc-800' : 'border-gray-200 hover:bg-gray-50'}`}
              aria-label="Account menu"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="text-sm font-bold hidden xl:block pr-2">{user?.name || 'Account'}</span>
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-[90]" onClick={()=> setShowUserMenu(false)} />
                  <motion.div
                    initial={{opacity: 0,y: 8}}
                    animate={{opacity: 1,y: 0}}
                    exit={{opacity: 0,y: 8}}
                    className={`absolute right-0 top-full mt-3 w-[min(16rem,calc(100vw-1.5rem))] rounded-2xl border shadow-2xl overflow-hidden z-[100] ${theme==='dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}
                  >
                    <div className="p-4 border-b border-gray-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                      <p className="text-sm font-black truncate">{user?.name}</p>
                      <p className="text-[10px] text-zinc-500 font-bold truncate mt-0.5">{user?.email}</p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/settings"
                        onClick={()=> setShowUserMenu(false)}
                        className="flex items-center space-x-3 w-full min-h-11 px-3 py-3 text-sm font-bold rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <SafeIcon icon={FiSettings} className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                      <button
                        onClick={()=> {signOut();setShowUserMenu(false);}}
                        className="flex items-center space-x-3 w-full min-h-11 px-3 py-3 text-sm font-black text-red-500 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      >
                        <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;